import React from 'react';
import styled from 'styled-components';
import Branded from '../components/Branded';

const Section = styled.section`
  background: #fff;
  padding: 120px 0;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 72px 0;
  }
`;

const Container = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
`;

const Header = styled.div`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 72px;

  @media (max-width: 768px) {
    margin-bottom: 48px;
  }

  .tag {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 18px;
    display: inline-flex;
    align-items: center;
    gap: 14px;

    &::before,
    &::after {
      content: '';
      width: 32px;
      height: 1px;
      background: rgba(240, 128, 32, 0.6);
    }
  }

  h2 {
    font-size: 2.4rem;
    font-weight: 700;
    color: #111;
    line-height: 1.15;
    letter-spacing: -0.015em;
    margin: 0;

    em {
      font-style: italic;
      font-weight: 400;
      color: #111;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
  }

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const CardWrap = styled.div`
  perspective: 1000px;
  height: 320px;

  @media (max-width: 900px) {
    height: 280px;
  }

  @media (max-width: 560px) {
    height: 240px;
  }
`;

const CardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  transform-style: preserve-3d;

  ${CardWrap}:hover & {
    transform: rotateY(180deg);
  }
`;

const CardFace = styled.div`
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  display: flex;
  flex-direction: column;
  padding: 32px 24px;
  overflow: hidden;
  border-radius: 8px;
`;

const CardFront = styled(CardFace)`
  background: #fff;
  border: 1px solid #e2e8f0;
  transition: border-color 0.4s ease, box-shadow 0.4s ease, transform 0.4s ease;
  padding: 0;
  justify-content: flex-end;

  ${CardWrap}:hover & {
    border-color: rgba(19, 120, 229, 0.4);
    box-shadow: 0 24px 50px rgba(11, 31, 58, 0.12);
  }

  .card-body {
    position: absolute;
    inset: 0;
  }

  .card-icon {
    width: 100%;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  /* dark gradient over the image bottom for the title */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.2) 35%,
      transparent 60%
    );
    pointer-events: none;
    z-index: 1;
  }

  .card-title {
    position: relative;
    z-index: 2;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
    line-height: 1.3;
    letter-spacing: -0.01em;
    text-align: left;
    padding: 20px;
  }
`;

const CardBack = styled(CardFace)`
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 24px 50px rgba(11, 31, 58, 0.12);
  transform: rotateY(180deg);
  justify-content: center;
  color: #1b2733;

  &::before {
    content: '';
    position: absolute;
    top: -40px;
    right: -40px;
    width: 140px;
    height: 140px;
    background: rgba(240, 128, 32, 0.18);
    border-radius: 50%;
    pointer-events: none;
  }

  .back-tag {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 12px;
    position: relative;
  }

  .card-desc {
    font-size: 13px;
    line-height: 1.7;
    color: #1b2733;
    font-weight: 300;
    position: relative;
  }
`;

const Cert = styled.div`
  margin: 96px auto 0;
  max-width: 640px;
  display: flex;
  align-items: center;
  gap: 28px;
  padding: 32px 36px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 18px 44px rgba(11, 31, 58, 0.08);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 18px;
    bottom: 18px;
    width: 3px;
    background: #f08020;
    border-radius: 0 2px 2px 0;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
    gap: 18px;
    padding: 28px 24px;
    margin-top: 56px;

    &::before {
      left: 18px;
      right: 18px;
      top: 0;
      bottom: auto;
      width: auto;
      height: 3px;
    }
  }

  .cert-logo {
    width: 120px;
    height: 120px;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .cert-content {
    flex: 1;

    .cert-tag {
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #f08020;
      margin-bottom: 6px;
    }

    .cert-title {
      font-size: 16px;
      font-weight: 600;
      color: #0b1f3a;
      margin-bottom: 6px;
      letter-spacing: -0.01em;
    }

    .cert-desc {
      font-size: 13px;
      color: #5b6b7c;
      line-height: 1.65;
    }
  }
`;

export default function Services({
  tag,
  title,
  certTitle,
  certDescription,
  certLogo,
  items,
}) {
  const data = items || [];

  if (!data.length) return null;

  return (
    <Section>
      <Container>
        <Header>
          {tag && <div className="tag">{tag}</div>}
          <h2>{title || 'Our Services'}</h2>
        </Header>

        <Grid>
          {data.map(({ _key, title: t, description, icon }) => (
            <CardWrap key={_key}>
              <CardInner>
                <CardFront>
                  <div className="card-body">
                    {icon?.asset?.url && (
                      <div className="card-icon">
                        <img src={icon.asset.url} alt={t} />
                      </div>
                    )}
                  </div>
                  <div className="card-title">{t}</div>
                </CardFront>
                <CardBack>
                  <div className="back-tag">{t}</div>
                  <Branded as="div" className="card-desc" text={description} />
                </CardBack>
              </CardInner>
            </CardWrap>
          ))}
        </Grid>

        {(certTitle || certLogo) && (
          <Cert>
            {certLogo?.asset?.url && (
              <div className="cert-logo">
                <img
                  src={certLogo.asset.url}
                  alt={certTitle || 'Certification'}
                />
              </div>
            )}
            <div className="cert-content">
              <div className="cert-tag">Certified</div>
              <Branded as="div" className="cert-title" text={certTitle} />
              <Branded as="div" className="cert-desc" text={certDescription} />
            </div>
          </Cert>
        )}
      </Container>
    </Section>
  );
}
